import { styled } from '@mui/material/styles';
import { TableContainer, Table, TableCell, TableRow } from '@mui/material';

// Styled components for the custom table
export const StyledTableContainer = styled(TableContainer)(() => ({
  overflowX: 'auto',
  border: '1px solid #E1E8ED',
  borderRadius: '8px',
}));

export const StyledTable = styled(Table)(() => ({
  width: '100%',
  borderCollapse: 'collapse',
  backgroundColor: '#F5F5F5',
}));

export const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '12px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: 0,
  position: 'sticky',
  zIndex: 1300,
  border: 'none',
  borderLeft: '1px solid #063142',
  borderRight: '1px solid #063142',
  borderBottom: '2px solid #063142',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  hyphens: 'auto',
}));

export const ComplexHeaderCell = styled(TableCell)<{ isGroupBorder?: boolean }>(({ theme, isGroupBorder }) => ({
  fontWeight: 700,
  fontSize: '12px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: 0,
  position: 'sticky',
  zIndex: 1300,
  border: 'none',
  borderBottom: '2px solid #063142',
  borderLeft: isGroupBorder ? '2px solid #063142' : '1px solid #063142',
  borderRight: '1px solid #063142',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  hyphens: 'auto',
}));

export const SubHeaderCell = styled(TableCell)<{ isGroupBorder?: boolean; isRightGroupBorder?: boolean }>(({ theme, isGroupBorder, isRightGroupBorder }) => ({
  fontWeight: 600,
  fontSize: '11px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: "2px",
  position: 'sticky',
  zIndex: 1298,
  border: 'none',
  borderBottom: '2px solid #063142',
  borderLeft: isGroupBorder ? '2px solid #063142' : '1px solid #063142',
  borderRight: isRightGroupBorder ? '2px solid #063142' : '1px solid #063142',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  hyphens: 'auto',
}));

export const DataCell = styled(TableCell)<{
  direction?: 'inbound' | 'outbound';
  isFirstRowOfGroup?: boolean;
}>(({ theme, direction, isFirstRowOfGroup }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor:
    direction === 'inbound' ? '#E6F5E6' : direction === 'outbound' ? '#F5E6E6' : '#FFFFFF',
  border: 'none',
  borderLeft: '1px solid #063142',
  borderRight: '1px solid #063142',
  borderBottom: '1px solid #ddd',
  borderTop: isFirstRowOfGroup ? '2px solid #063142' : 'none',
  padding: "2px",
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 500,
  },
}));

export const ComplexDataCell = styled(TableCell)<{
  direction?: 'inbound' | 'outbound';
  isBilateral?: boolean;
  hasError?: boolean;
  isDisabled?: boolean;
  isGroupBorder?: boolean;
  isRightGroupBorder?: boolean;
  isFirstRowOfGroup?: boolean;
  hasAlertA?: boolean;
  hasAlertB?: boolean;
}>(({ theme, direction, isBilateral, hasError, isDisabled, isGroupBorder, isRightGroupBorder, isFirstRowOfGroup, hasAlertA, hasAlertB }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: isDisabled
    ? '#A7C0B4' // Use the specified color for disabled fields
    : hasError
      ? '#FFEBEE' // Light red background for error state
      : hasAlertA
        ? '#F44336' // Strong red background for alert A
        : hasAlertB
          ? '#F44336' // Strong red background for alert B
          : direction === 'inbound'
            ? '#E6F5E6'
            : direction === 'outbound'
              ? '#F5E6E6'
              : '#FFFFFF',
  border: 'none',
  borderLeft: isGroupBorder ? '2px solid #063142' : '1px solid #063142',
  borderRight: isRightGroupBorder ? '2px solid #063142' : '1px solid #063142',
  borderBottom: '1px solid #ddd',
  borderTop: isFirstRowOfGroup ? '2px solid #063142' : 'none',
  padding: "2px",
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 500,
  },
  // Styling for bilateral data when original value exists
  ...(isBilateral && {
    fontStyle: 'italic',
    color: '#666666',
    opacity: 0.7,
  }),
  // Error state styling
  ...(hasError && {
    color: '#D32F2F',
    fontWeight: 600,
    border: '2px solid #D32F2F',
  }),
  // Disabled state styling
  ...(isDisabled && {
    color: '#666666',
    cursor: 'context-menu',
  }),
  // Styling for bilateral data when original value exists
  ...(isBilateral &&
    !isDisabled && 
    !hasError && 
    !hasAlertA && 
    !hasAlertB && {
      fontStyle: 'italic',
      color: '#666666',
      opacity: 0.7,
    }),
  // Alert A styling - red background and border
  ...(hasAlertA &&
    !isDisabled && {
      backgroundColor: '#F44336', // Strong red background for alert A
      borderColor: '#D32F2F', // Darker red border for alert A
      color: '#000000', // Black text for alert A
      fontWeight: 600,
    }),
  // Alert B styling - red background and border
  ...(hasAlertB &&
    !isDisabled && {
      backgroundColor: '#F44336', // Strong red background for alert B
      borderColor: '#D32F2F', // Darker red border for alert B
      color: '#000000', // Black text for alert B
      fontWeight: 600,
    }),
}));

export const GroupSeparatorRow = styled(TableRow)(() => ({
  '& td': {
    borderBottom: '2px solid #063142',
    backgroundColor: '#F5F5F5',
    height: '4px',
    padding: "2px",
  },
}));

export const TotalSectionHeaderRow = styled(TableRow)(() => ({
  backgroundColor: '#E7EFF5',
  '& td': {
    backgroundColor: '#E7EFF5 !important',
    color: '#063142',
  },
}));